import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';
import { useLab } from '../i18n/LabContext';

const CAT_KEYS = [
  { key: 'ijara',        icon: '🏠', tKey: 'cat_ijara' },
  { key: 'oylik',        icon: '👤', tKey: 'cat_oylik' },
  { key: 'elektr',       icon: '⚡', tKey: 'cat_elektr' },
  { key: 'internet',     icon: '🌐', tKey: 'cat_internet' },
  { key: 'jihoz',        icon: '🔧', tKey: 'cat_jihoz' },
  { key: 'snack_supply', icon: '🛒', tKey: 'cat_snack' },
  { key: 'marketing',    icon: '📢', tKey: 'cat_marketing' },
  { key: "ta'mir",       icon: '🔨', tKey: 'cat_tamir' },
  { key: 'soliq',        icon: '🏛️', tKey: 'cat_soliq' },
  { key: 'boshqa',       icon: '📋', tKey: 'cat_boshqa' },
];

const getCategories = (t) => CAT_KEYS.map(c => ({ ...c, label: t(c.tKey) }));
const fmt = n => Number(n || 0).toLocaleString();
const fmtDate = d => d ? new Date(d).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

function ExpenseForm({ initial, onSave, onCancel }) {
  const { t } = useLang();
  const CATEGORIES = getCategories(t);
  const [form, setForm] = useState({
    category: initial?.category || 'ijara',
    amount: initial?.amount || '',
    description: initial?.description || '',
    expense_date: initial?.expense_date?.split('T')[0] || new Date().toISOString().split('T')[0],
    is_recurring: initial?.is_recurring || false,
    recur_day: initial?.recur_day || '',
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.amount || !form.category) return alert(t('alertAmountRequired'));
    setLoading(true);
    try {
      if (initial?.id) {
        await api.put(`/expenses/${initial.id}`, form);
      } else {
        await api.post('/expenses', form);
      }
      onSave();
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const inp = (label, type, val, onChange, opts = {}) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>{label}</label>
      <input type={type} value={val} onChange={e => onChange(e.target.value)} {...opts}
        style={{ width: '100%', background: 'var(--card)', border: '1px solid var(--border)',
          color: 'var(--text)', padding: '9px 13px', borderRadius: 9, fontSize: 13,
          outline: 'none', boxSizing: 'border-box' }} />
    </div>
  );

  return (
    <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24 }}>
      <h3 style={{ margin: '0 0 20px', fontSize: 16 }}>{initial?.id ? `✏️ ${t('editExpenseTitle')}` : `➕ ${t('newExpenseTitle')}`}</h3>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>{t('categoryLabel')}</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
          {CATEGORIES.map(c => (
            <button key={c.key} onClick={() => set('category', c.key)} style={{
              padding: '8px 4px', borderRadius: 9, cursor: 'pointer', fontSize: 12, textAlign: 'center',
              background: form.category === c.key ? 'rgba(0,212,255,0.12)' : 'var(--card)',
              border: `1px solid ${form.category === c.key ? 'var(--cyan)' : 'var(--border)'}`,
              color: form.category === c.key ? 'var(--cyan)' : 'var(--text2)',
            }}>
              <div style={{ fontSize: 16 }}>{c.icon}</div>
              <div style={{ fontSize: 10, marginTop: 2 }}>{c.label}</div>
            </button>
          ))}
        </div>
      </div>

      {inp(t('amountLabel'), 'number', form.amount, v => set('amount', v), { placeholder: '0', min: '0' })}
      {inp(t('descLabel'), 'text', form.description, v => set('description', v), { placeholder: t('optionalPlaceholder') })}
      {inp(t('dateLabel'), 'date', form.expense_date, v => set('expense_date', v))}

      {/* Takrorlanuvchi */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={form.is_recurring} onChange={e => set('is_recurring', e.target.checked)}
            style={{ width: 16, height: 16 }} />
          {t('recurMonthly')}
        </label>
        {form.is_recurring && (
          <input type="number" value={form.recur_day} onChange={e => set('recur_day', e.target.value)}
            placeholder="Oy kuni (1-28)" min="1" max="28"
            style={{ width: 130, background: 'var(--card)', border: '1px solid var(--border)',
              color: 'var(--text)', padding: '7px 11px', borderRadius: 8, fontSize: 12, outline: 'none' }} />
        )}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCancel} style={{ flex: 1, background: 'transparent', border: '1px solid var(--border)',
          color: 'var(--text2)', padding: '10px', borderRadius: 9, cursor: 'pointer', fontSize: 13 }}>
          {t('cancel')}
        </button>
        <button onClick={handleSave} disabled={loading} style={{ flex: 2,
          background: 'rgba(0,255,136,0.12)', border: '1px solid var(--green)',
          color: 'var(--green)', padding: '10px', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
          {loading ? t('saving') : `💾 ${t('save')}`}
        </button>
      </div>
    </div>
  );
}

export default function Expenses() {
  const { t } = useLang();
  const { mode: labMode } = useLab();
  const isAllMode = labMode === 'all';
  const CATEGORIES = getCategories(t);
  const catMeta = (key) => CATEGORIES.find(c => c.key === key) || { icon: '📋', label: key };
  const now = new Date();
  const [from, setFrom] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
  const [to, setTo]     = useState(now.toISOString().split('T')[0]);
  const [catFilter, setCatFilter] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = `from=${from}&to=${to}${catFilter ? `&category=${catFilter}` : ''}`;
      const [expRes, sumRes] = await Promise.all([
        api.get(`/expenses?${params}`).then(r => r.data),
        api.get(`/expenses/summary?from=${from}&to=${to}`).then(r => r.data),
      ]);
      setExpenses(expRes);
      setSummary(sumRes);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [from, to, catFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await api.delete(`/expenses/${id}`);
      load();
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t('expensesTitle')}</h1>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>{t('expensesSubtitle')}</div>
        </div>
        {!showForm && !isAllMode && (
          <button onClick={() => { setEditing(null); setShowForm(true); }} style={{
            background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)',
            color: 'var(--green)', padding: '10px 22px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700,
          }}>➕ {t('addExpenseBtn')}</button>
        )}
        {isAllMode && (
          <div style={{
            background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 10,
            padding: '10px 16px', fontSize: 12, color: 'var(--text2)',
          }}>🌐 Hammasi — xarajat qo'shish uchun klub tanlang</div>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ marginBottom: 24 }}>
          <ExpenseForm
            initial={editing}
            onSave={() => { setShowForm(false); setEditing(null); load(); }}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        </div>
      )}

      {/* Yig'indi kartalar */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
          <div style={{ background: 'rgba(255,59,92,0.08)', border: '1px solid rgba(255,59,92,0.3)',
            borderRadius: 12, padding: '16px 20px', gridColumn: '1 / -1',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 700 }}>💸 {t('totalExpenses')}</span>
            <span style={{ fontSize: 20, fontWeight: 900, color: 'var(--red)', fontFamily: 'Orbitron, sans-serif' }}>
              {fmt(summary.total)} {t('currency')}
            </span>
          </div>
          {summary.categories?.map(c => {
            const meta = catMeta(c.category);
            return (
              <div key={c.category} onClick={() => setCatFilter(catFilter === c.category ? '' : c.category)}
                style={{ background: catFilter === c.category ? 'rgba(0,212,255,0.08)' : 'var(--card2)',
                  border: `1px solid ${catFilter === c.category ? 'var(--cyan)' : 'var(--border)'}`,
                  borderRadius: 12, padding: '14px 16px', cursor: 'pointer' }}>
                <div style={{ fontSize: 22, marginBottom: 6 }}>{meta.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{(c.total || 0).toLocaleString()}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>{meta.label} ({c.cnt} ta)</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtrlar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)}
          style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)',
            padding: '8px 12px', borderRadius: 9, fontSize: 12, outline: 'none' }} />
        <span style={{ color: 'var(--text3)' }}>—</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)}
          style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)',
            padding: '8px 12px', borderRadius: 9, fontSize: 12, outline: 'none' }} />
        {catFilter && (
          <button onClick={() => setCatFilter('')} style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12,
            background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)', color: 'var(--cyan)', cursor: 'pointer' }}>
            ✕ {catMeta(catFilter).label}
          </button>
        )}
      </div>

      {/* Jadval */}
      <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>{t('loading')}</div>
        ) : expenses.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>💸</div>
            <div style={{ fontSize: 15, color: 'var(--text2)' }}>{t('noExpenses')}</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 6 }}>{t('noExpensesHint')}</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--card)' }}>
                {[t('tableDate'), t('tableCategory'), t('tableDesc'), t('tableAmount'), t('tableRecurring'), t('tableActions')].map(h => (
                  <th key={h} style={{ padding: '12px 20px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expenses.map(e => {
                const meta = catMeta(e.category);
                return (
                  <tr key={e.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 20px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                      {fmtDate(e.expense_date)}
                    </td>
                    <td style={{ padding: '12px 20px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span>{meta.icon}</span>
                        <span style={{ fontWeight: 600 }}>{meta.label}</span>
                      </span>
                    </td>
                    <td style={{ padding: '12px 20px', color: 'var(--text2)', maxWidth: 220,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.description || '—'}
                    </td>
                    <td style={{ padding: '12px 20px', color: 'var(--red)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {fmt(e.amount)} {t('currency')}
                    </td>
                    <td style={{ padding: '12px 20px' }}>
                      {e.is_recurring ? (
                        <span style={{ fontSize: 11, background: 'rgba(159,122,234,0.15)', color: 'var(--purple)',
                          border: '1px solid rgba(159,122,234,0.4)', padding: '3px 10px', borderRadius: 20 }}>
                          🔄 {t('recurEveryMonth')}{e.recur_day ? `, ${e.recur_day}` : ''}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 20px' }}>
                      {isAllMode ? (
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>🌐</span>
                      ) : (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => { setEditing(e); setShowForm(true); }} style={{
                            background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
                            padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>✏️</button>
                          <button onClick={() => handleDelete(e.id)} style={{
                            background: 'transparent', border: '1px solid rgba(255,59,92,0.4)', color: 'var(--red)',
                            padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
